import { useApolloClient } from "@apollo/client";
import cuid from "cuid";
import { Formik } from "formik";
import { defaultsDeep, filter, find, get, isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdArrowForwardIos } from "react-icons/md";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Header from "shared/components/Header";
import Loading from "shared/components/Spin";
import { useQuery } from "shared/hooks/useApi";
import { fetchZipCodeCities } from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import {
  ACORIS_PHONE_PREFIXES,
  CONTACT_POINT_SYSTEM,
  RELATIONSHIP,
} from "utils/constants";
import {
  getMaxDeltaEnd,
  getMinDeltaStart,
  getRetroactivityEnabled,
} from "utils/helpers/array";
import { getRetroactivityDays } from "screens/Private/Broker/Projects/Detail/Templates/Manual/AcorisConseiller/retroactivity";
import { isAcorisRiaBlocked } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/riaBlock";
import {
  buildAcorisTerminationRequestPayload,
  resolveAcorisContractMoreThanOneYear,
  resolveAcorisHasOtherInsurer,
  resolveAcorisTerminationRequest,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/terminationRequest";
import { convertToTouched } from "utils/helpers/input";
import ExpirationModal from "./Widgets/ExpirationModal";
import Steps from "./Widgets/Steps";
import validation from "./validation";

const STEPS_KEYS = {
  CONTRACT: "CONTRACT",
  ADHERENT: "ADHERENT",
  SPOUSE: "SPOUSE",
  CHILDREN: "CHILDREN",
};

const MIN_DELTA_START_RIA = 34;

const Contact = ({ onNext, project }) => {
  const client = useApolloClient();
  const [deltaRia, setDeltaRia] = useState(30);
  const [deltaStart, setDeltaStart] = useState(1);
  const [deltaEndRia, setDeltaEndRia] = useState();
  const [deltaEnd, setDeltaEnd] = useState();
  const [retroactivityEnabled, setRetroactivityEnabled] = useState(false);
  const [retroactivityDays, setRetroactivityDays] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [nextLoading, setNextLoading] = useState(false);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const [city, setCity] = useState({
    id: cuid(),
    system: CONTACT_POINT_SYSTEM.CITY,
    value: null,
  });
  useQuery(INSURANCE_PLANS, {
    variables: { where: {} },
    onCompleted: ({ insurancePlans: { data: insurancePlans } }) => {
      const nextDeltaRia = getMinDeltaStart({ insurancePlans, minValue: 30 });
      const nextDeltaStart = getMinDeltaStart({
        insurancePlans,
        minValue: 1,
        key: "DELTA_START",
      });
      const nextDeltaEndRia = getMaxDeltaEnd({ insurancePlans }) || 0;
      const nextDeltaEnd =
        getMaxDeltaEnd({
          insurancePlans,
          key: "DELTA_END",
        }) || 0;
      setDeltaRia(Math.max(nextDeltaRia, MIN_DELTA_START_RIA));
      setDeltaStart(nextDeltaStart);
      setDeltaEndRia(nextDeltaEndRia);
      setDeltaEnd(nextDeltaEnd);
      setRetroactivityEnabled(getRetroactivityEnabled({ insurancePlans }));
      setRetroactivityDays(getRetroactivityDays({ insurancePlans }));
      setLoading(false);
    },
  });

  useEffect(() => {
    if (get(project, "expired")) setOpen(true);
  }, [project]);

  const spouse = find(get(project, "contact.relatedPersons", []), {
    relationship: RELATIONSHIP.SPOUSE,
  });
  const initialHasOtherInsurer = resolveAcorisHasOtherInsurer(project);
  const initialOlderThanOneYear = resolveAcorisContractMoreThanOneYear(project);
  const initialTerminationRequest = resolveAcorisTerminationRequest(project);

  const initialValues = useMemo(
    () =>
      defaultsDeep(
        {
          contract: { ...project.contract },
          contact: { ...project.contact },
          children: filter(get(project, "contact.relatedPersons", []), {
            relationship: RELATIONSHIP.CHILD,
          }),
          spouse,
          zipCode: find(get(project, "contact.telecoms", []), {
            system: CONTACT_POINT_SYSTEM.ZIP_CODE,
          }),
          phoneNumber: find(get(project, "contact.telecoms", []), {
            system: CONTACT_POINT_SYSTEM.PHONE,
          }),
          phoneNumberPrefix: find(get(project, "contact.telecoms", []), {
            system: CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX,
          }),
          fields: { ...get(project, "fields", {}) },
          hasOtherInsurer: initialHasOtherInsurer,
          contractOlderThanOneYear: initialOlderThanOneYear,
          terminationRequest: initialTerminationRequest,
          ria: initialOlderThanOneYear,
          retroactive: retroactivityEnabled && project.retroactive === true,
          locked: project.locked,
          expired: project.expired,
        },
        {
          ria: false,
          retroactive: false,
          locked: false,
          hasSpouse: !!spouse,
          hasOtherInsurer: false,
          contractOlderThanOneYear: false,
          terminationRequest: null,
          children: [],
          fields: { twoTimesSale: false },
          spouse: {
            id: cuid(),
            relationship: RELATIONSHIP.SPOUSE,
            identity: {
              id: cuid(),
              socialRegime: null,
              user: {
                id: cuid(),
                gender: null,
                firstname: null,
                lastname: null,
                birthDate: null,
              },
            },
          },
          zipCode: {
            id: cuid(),
            system: CONTACT_POINT_SYSTEM.ZIP_CODE,
            value: null,
          },
          phoneNumber: {
            id: cuid(),
            system: CONTACT_POINT_SYSTEM.PHONE,
            value: null,
          },
          phoneNumberPrefix: {
            id: cuid(),
            system: CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX,
            value: ACORIS_PHONE_PREFIXES[0].value,
          },
          contract: { id: cuid(), issuanceDate: null },
          contact: {
            id: cuid(),
            socialRegime: null,
            fields: {
              profile: null,
              isClient: false,
            },
            user: {
              id: cuid(),
              gender: null,
              firstname: null,
              lastname: null,
              birthDate: null,
            },
          },
        },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [retroactivityEnabled],
  );

  const validationSchema = useMemo(
    () =>
      validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
        {
          fetchZipCodeCities: (zipCode) => fetchZipCodeCities(client, zipCode),
          setCity,
          deltaRia,
          deltaStart,
          retroactivityDays,
        },
      ),
    [t, client, deltaRia, deltaStart, retroactivityDays],
  );

  const onSubmit = (record) => {
    setNextLoading(true);
    const {
      hasSpouse,
      spouse: spouseValue,
      children,
      zipCode,
      phoneNumber,
      phoneNumberPrefix,
      contact,
      hasOtherInsurer,
      contractOlderThanOneYear,
      terminationRequest,
      fields: formFields,
      ...rest
    } = record;
    const isRia = contractOlderThanOneYear === true;
    const deltaS = isRia ? deltaRia : deltaStart;
    const deltaE = isRia ? deltaEndRia : deltaEnd;
    const profile = get(contact, "fields.profile");
    onNext({
      payload: {
        ...rest,
        ria: isRia,
        terminationRequest: buildAcorisTerminationRequestPayload({
          terminationRequest,
          contact,
          isRia,
        }),
        contact: {
          ...contact,
          user: contact.user,
          relatedPersons: [...(hasSpouse ? [spouseValue] : []), ...children],
          fields: {
            ...get(contact, "fields", {}),
            profile,
            isClient: get(contact, "fields.isClient") === true,
            hasOtherInsurer: hasOtherInsurer === true,
            contractOlderThanOneYear: isRia,
          },
          telecoms: [
            ...get(contact, "telecoms", []).filter(
              ({ system }) =>
                ![
                  CONTACT_POINT_SYSTEM.ZIP_CODE,
                  CONTACT_POINT_SYSTEM.PHONE,
                  CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX,
                  CONTACT_POINT_SYSTEM.CITY,
                ].includes(system),
            ),
            zipCode,
            phoneNumber,
            phoneNumberPrefix,
            city,
          ],
        },
        expired: false,
        visible: true,
        fields: {
          ...get(project, "fields", {}),
          ...formFields,
          profileType: profile,
          contractMoreThanOneYear: isRia,
          hasOtherInsurer: hasOtherInsurer === true,
          selfOccupation: isRia ? false : undefined,
          deltaStart: deltaS,
          deltaEnd: deltaE,
        },
      },
      onCompleted: () => setNextLoading(false),
      onError: () => setNextLoading(false),
    });
  };

  if (loading) return <Loading />;

  return (
    <>
      <Header key="header" title={DETAIL.TEMPLATES.CONTACT.TITLE} />
      <Formik
        validateOnMount
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ handleSubmit, values, ...formProps }) => {
          const riaBlocked = isAcorisRiaBlocked({
            hasOtherInsurer: values.hasOtherInsurer,
            contractOlderThanOneYear: values.contractOlderThanOneYear,
          });
          return [
            <ExpirationModal
              key="expiration-modal"
              open={open}
              setOpen={setOpen}
              values={values}
              deltaRia={deltaRia}
              deltaStart={deltaStart}
              retroactivityDays={retroactivityDays}
              formProps={formProps}
            />,
            <div className="contact-form" key="form_area">
              <div className="contact-form__content">
                <Form
                  title={DETAIL.TEMPLATES.CONTACT.TITLE}
                  description={DETAIL.TEMPLATES.CONTACT.DESCRIPTION}
                >
                  {Object.keys(STEPS_KEYS).map(
                    (stepKey, index) =>
                      Steps({
                        index: index + 1,
                        values,
                        translation: DETAIL,
                        deltaRia,
                        deltaStart,
                        deltaEnd,
                        deltaEndRia,
                        retroactivityEnabled,
                        retroactivityDays,
                        ...formProps,
                      })[stepKey],
                  )}
                </Form>
              </div>
              <div className="contact-form__action">
                <Button
                  type="primary"
                  onClick={async () => {
                    const errors = await formProps.validateForm();
                    await formProps.setTouched(convertToTouched(errors), true);
                    if (isEmpty(errors)) handleSubmit();
                  }}
                  loading={nextLoading}
                  disabled={nextLoading || riaBlocked}
                >
                  {DETAIL.TEMPLATES.CONTACT.SUBMIT}
                  <MdArrowForwardIos size={20} />
                </Button>
              </div>
            </div>,
          ];
        }}
      </Formik>
    </>
  );
};

export default Contact;
